import { RuntimeFrame } from './AnimationTypes';

export const performMatrixTransformDraw = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    frame: RuntimeFrame,
    alpha: number,
    engine?: any,
): void => {
    if (
        !frame.image ||
        !frame.image.complete ||
        frame.image.naturalWidth === 0
    ) {
        return;
    }

    const activeIndex =
        typeof engine?.alignmentIndex === 'number' ? engine.alignmentIndex : 0;

    const align = frame.meta?.alignments?.[activeIndex] ??
        frame.meta?.alignments?.[0] ?? {
            offsetX: 0,
            offsetY: 0,
            scale: 1,
            roll: 0,
        };

    // Convert normalized percentage offsets to actual canvas pixel shifts
    const pixelOffsetX = canvas.width * ((align.offsetX ?? 0) / 100.0);
    const pixelOffsetY = canvas.height * ((align.offsetY ?? 0) / 100.0);
    const scale = align.scale ?? 1;
    const roll = align.roll ?? 0;

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    ctx.save();
    ctx.globalAlpha = alpha;

    // 1. Position cursor at center plus the scaled pixel offset
    ctx.translate(cx + pixelOffsetX, cy + pixelOffsetY);

    if (roll !== 0) {
        ctx.rotate((roll * Math.PI) / 180);
    }
    if (scale !== 1) {
        ctx.scale(scale, scale);
    }

    // 2. Draw asset centered at transform anchor
    const renderW = canvas.width;
    const renderH = canvas.height;

    ctx.drawImage(frame.image, -renderW / 2, -renderH / 2, renderW, renderH);

    ctx.restore();
};

export const executeFrameBlendLayers = (engine: any, now: number): void => {
    if (!engine.canvas || !engine.ctx) return;

    if (engine.blendStartTime === null || engine.prevIndex === null) {
        engine.renderFrameToCanvas(engine.currentIndex);
        return;
    }

    // Ensure frames needed for blending are loaded
    if (typeof engine.ensureFrameLoaded === 'function') {
        engine.ensureFrameLoaded(engine.currentIndex);
        engine.ensureFrameLoaded(engine.prevIndex);
    }

    const timePassed = now - engine.blendStartTime;
    let progress = 1.0;

    if (engine.blendDuration > 0) {
        progress = Math.min(1.0, timePassed / engine.blendDuration);
    }

    engine.ctx.clearRect(0, 0, engine.canvas.width, engine.canvas.height);

    // Fade Out Old Frame A
    const frameA = engine.runtimeFrames[engine.prevIndex];
    if (frameA && frameA.isReady && progress < 1.0) {
        performMatrixTransformDraw(
            engine.ctx,
            engine.canvas,
            frameA,
            1.0 - progress,
            engine,
        );
    }

    // Fade In Target Frame B
    const frameB = engine.runtimeFrames[engine.currentIndex];
    if (frameB && frameB.isReady) {
        performMatrixTransformDraw(
            engine.ctx,
            engine.canvas,
            frameB,
            progress,
            engine,
        );
    }

    if (progress >= 1.0) {
        engine.blendStartTime = null;
        engine.prevIndex = null;

        if (engine.isWaitingToPauseFinish) {
            engine.pause();
        }
    }
};
